import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { MINUTE } from 'time-constants';
import { number, object, string } from 'yup';

import { apiConfig } from '@constants';
import { currentLocationWeatherState, hasResetRefetchIntervalState } from '@store';

import { refetchIntervalByExpirationTime } from '../../helpers/refetchIntervalUtils';
import { toQueryString } from '../../helpers/StringUtils';
import { useGetUserLocation } from '../useGetUserLocation';

/**
 * Example of a TWC API schema converted to Yup types
 * Anything marked with * (decimal and epoch) are not types Yup natively supports, so might want to add some additional validation for them
 * This isn't finalized, based required and nullable fields on TWC API spec but might be incorrect, keep an eye out for errors/test thoroughly - anything with .nullable is not required and can be null per TWC API
 */
const currentOnDemandSchema = object({
  cloudCeiling: number().integer().nullable(true), // integer
  cloudCoverPhrase: string().required(), // string
  dayOfWeek: string().required(), // string
  dayOrNight: string().required(), // string
  expirationTimeUtc: number().integer(), // epoch*
  iconCode: number().integer(), // integer
  iconCodeExtend: number().integer(), // integer
  obsQualifierCode: string().nullable(true), // string
  obsQualifierSeverity: number().integer().nullable(true), // integer
  precip1Hour: number().nullable(true), // decimal*
  precip6Hour: number().nullable(true), // decimal*
  precip24Hour: number().nullable(true), // decimal*
  pressureAltimeter: number().required(), // decimal*
  pressureChange: number().required(), // decimal*
  pressureMeanSeaLevel: number().required(), // decimal*
  pressureTendencyCode: number().integer(), // integer
  pressureTendencyTrend: string().required(), // string
  relativeHumidity: number().integer(), // integer
  snow1Hour: number().nullable(true), // decimal*
  snow6Hour: number().nullable(true), // decimal*
  snow24Hour: number().nullable(true), // decimal*
  sunriseTimeLocal: string(), // ISO
  sunriseTimeUtc: number().integer().nullable(true), // epoch*
  sunsetTimeLocal: string(), // ISO
  sunsetTimeUtc: number().integer().nullable(true), // epoch*
  temperature: number().integer(), // integer
  temperatureChange24Hour: number().integer(), // integer
  temperatureDewPoint: number().integer(), // integer
  temperatureFeelsLike: number().integer(), // integer
  temperatureHeatIndex: number().integer(), // integer
  temperatureMax24Hour: number().integer(), // integer
  temperatureMaxSince7Am: number().integer(), // integer
  temperatureMin24Hour: number().integer(), // integer
  temperatureWindChill: number().integer(), // integer
  uvDescription: string().required(), // string
  uvIndex: number().integer(), // integer
  validTimeLocal: string().required(), // ISO
  validTimeUtc: number().integer(), // epoch*
  visibility: number().required(), // decimal*
  windDirection: number().integer(), // integer
  windDirectionCardinal: string().required(), // string
  windGust: number().integer().nullable(true), // integer
  windSpeed: number().integer(), // integer
  wxPhraseLong: string().required(), // string
  wxPhraseMedium: string().nullable(true), // string - see currentWeatherConditionsType.js; this property is still present in API but being phased out, might have to remove later
  wxPhraseShort: string().nullable(true), // string
});

const argumentsSchema = object({
  lat: number().required(),
  lng: number().required(),
  language: string().default('en-US'),
});

const getApiUrl = ({ lat, lng, language }) =>
  [
    apiConfig.TWC_API_BASE,
    apiConfig.TWC_API_CURRENT_ON_DEMAND,
    toQueryString({
      language,
      apiKey: apiConfig.TWC_API_KEY,
      geocode: `${lat},${lng}`,
      units: 'e',
      format: 'json',
    }),
  ].join('');

/**
 * Latitude and longitude coordinates will presumably be provided by geolocation
 * This looks like best available package (need to investigate if it works for React Native Web or if need browser geolocation API) - https://github.com/Agontuk/react-native-geolocation-service
 * Will get coords and store in context on App load, and use useContext to get it in the hook here probably?
 * (Using the local coords of where I am in South Carolina for now)
 * The "currents" API also offers IATA, ICAO, Place ID, and Postal Key query params so we will have to see which ones we need to use; may want to share the common stuff like units, language, format between them (have the hook take in a param for what type of query param to use)
 */
const getCurrentLocationWeather = async ({ lat, lng }) => {
  const validateArguments = await argumentsSchema.validate({ lat, lng });

  const url = getApiUrl(validateArguments);
  const { data } = await axios.get(url);

  return currentOnDemandSchema.validate(data);
};

export const useGetCurrentLocationWeather = () => {
  const {
    coords: { lat, lng },
  } = useGetUserLocation();
  const queryClient = useQueryClient();
  const [state, setState] = useRecoilState(currentLocationWeatherState);
  const [refetchInterval, setRefetchInterval] = useState(10 * MINUTE);

  const [hasResetRefetchInterval, setHasResetRefetchInterval] = useRecoilState(hasResetRefetchIntervalState);

  const { currentLocationWeather: currentLocatedWeatherRefetched } = hasResetRefetchInterval;

  // Trigger refetch when Debug date is set
  useEffect(() => {
    if (Date.prototype.debugDate && !currentLocatedWeatherRefetched) {
      console.log('Invalidating / Refetching Current Location Weather');
      // Invalidating query to trigger refetch since we don't have access to
      // expirationTimeUtc in the onSuccess to call the refetch fn directly
      queryClient.invalidateQueries(['getCurrentLocationWeather', lat, lng]);
      setHasResetRefetchInterval({
        ...hasResetRefetchInterval,
        currentLocationWeather: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Date.prototype.debugDate]);

  const { data, error, isLoading, isFetched } = useQuery(
    ['getCurrentLocationWeather', lat, lng],
    () => getCurrentLocationWeather({ lat, lng }),
    {
      enabled: Boolean(lat && lng),
      refetchOnWindowFocus: false,
      refetchInterval,
      retry: false,
      onSettled: _data => {
        if (_data?.expirationTimeUtc) {
          refetchIntervalByExpirationTime({
            expirationTimeUtc: _data.expirationTimeUtc,
            setRefetchInterval,
          });
        } else {
          setRefetchInterval(2 * MINUTE);
        }
      },
    },
  );

  useEffect(() => {
    setState({ data, error, isFetched, isLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, isFetched]);

  return state;
};
